import {
  forwardRef,
  type HTMLAttributes,
  type AnchorHTMLAttributes,
  type Ref,
} from 'react';
import { cva, type VariantProps } from 'class-variance-authority';
import { cn } from '../../../utils';

/**
 * Typography - Unified text styling system for Agion Design System
 *
 * Colours are theme tokens (agion/Agion#3256), so each variant follows the
 * current theme (globals.css) and the next theme (theme-next.css), light and dark:
 * - Primary, secondary, muted text: --text-primary, --text-secondary, --text-muted
 * - Accent text: the brand and status ramps (brand, success, warning, error, info)
 *
 * WCAG AA compliant contrast ratios maintained across all variants.
 */

/* ========================================
   HEADING VARIANTS
   ======================================== */

export const headingVariants = cva('font-semibold tracking-tight', {
  variants: {
    level: {
      h1: 'text-3xl font-bold',
      h2: 'text-2xl font-semibold',
      h3: 'text-xl font-semibold',
      h4: 'text-lg font-medium',
      h5: 'text-base font-medium',
      h6: 'text-sm font-medium',
    },
    color: {
      default: 'text-text-primary',
      muted: 'text-text-muted',
      brand: 'text-brand-800',
      success: 'text-success-700',
      warning: 'text-warning-700',
      error: 'text-error-700',
      info: 'text-info-700',
    },
  },
  defaultVariants: {
    level: 'h2',
    color: 'default',
  },
});

export type HeadingVariants = VariantProps<typeof headingVariants>;

export interface HeadingProps
  extends Omit<HTMLAttributes<HTMLHeadingElement>, 'color'>,
    HeadingVariants {
  as?: 'h1' | 'h2' | 'h3' | 'h4' | 'h5' | 'h6';
}

export const Heading = forwardRef<HTMLHeadingElement, HeadingProps>(
  ({ level = 'h2', color, as, className, children, ...props }, ref) => {
    const Component = as || level || 'h2';
    return (
      <Component
        ref={ref}
        className={cn(headingVariants({ level, color }), className)}
        {...props}
      >
        {children}
      </Component>
    );
  }
);
Heading.displayName = 'Heading';

/* ========================================
   TEXT/PARAGRAPH VARIANTS
   ======================================== */

export const textVariants = cva('', {
  variants: {
    size: {
      xs: 'text-xs',
      sm: 'text-sm',
      base: 'text-base',
      lg: 'text-lg',
      xl: 'text-xl',
    },
    weight: {
      normal: 'font-normal',
      medium: 'font-medium',
      semibold: 'font-semibold',
      bold: 'font-bold',
    },
    color: {
      // Primary - main content text
      default: 'text-text-primary',
      // Secondary - supporting text
      secondary: 'text-text-secondary',
      // Muted - de-emphasized text. slate-500 on white measured 4.27-4.66:1 depending on AA
      // hinting — under the 4.5:1 AA floor for normal text (verify-ds ink-on-fill, light only;
      // dark already passes at slate-400 on the dark surface). slate-600 on white measures
      // 7.65:1, comfortably clear, and is still visibly lighter than the slate-700 "secondary"
      // tier, so the muted/secondary/default hierarchy stays intact. --text-muted carries
      // those steps in the current theme.
      muted: 'text-text-muted',
      // Subtle - very light emphasis. slate-400 on white measured 2.28-2.63:1 and dark's
      // slate-500 on the dark surface measured 3.17:1 (verify-ds ink-on-fill, both themes —
      // StatCard labels "Active Missions"/"Active Agents"/"Success Rate"). Below slate-600
      // (light) / slate-400 (dark) nothing on this ramp clears 4.5:1 against these surfaces,
      // which is also where `muted` above had to land — there is no shade lighter than muted
      // that still passes AA on these backgrounds, so subtle can no longer be visually distinct
      // from muted. Same values as `muted`, not a typo.
      subtle: 'text-text-muted',
      // Semantic colors
      brand: 'text-brand-800',
      success: 'text-success-700',
      warning: 'text-warning-700',
      error: 'text-error-700',
      info: 'text-info-700',
      // Link color
      link: 'text-text-primary hover:text-text-muted',
    },
    leading: {
      tight: 'leading-tight',
      snug: 'leading-snug',
      normal: 'leading-normal',
      relaxed: 'leading-relaxed',
      loose: 'leading-loose',
    },
  },
  defaultVariants: {
    size: 'sm',
    weight: 'normal',
    color: 'default',
    leading: 'normal',
  },
});

export type TextVariants = VariantProps<typeof textVariants>;

export interface TextProps
  extends Omit<HTMLAttributes<HTMLParagraphElement>, 'color'>,
    TextVariants {
  as?: 'p' | 'span' | 'div';
}

export const Text = forwardRef<HTMLParagraphElement, TextProps>(
  ({ size, weight, color, leading, as = 'p', className, children, ...props }, ref) => {
    const Component = as;
    return (
      <Component
        ref={ref as Ref<HTMLParagraphElement>}
        className={cn(textVariants({ size, weight, color, leading }), className)}
        {...props}
      >
        {children}
      </Component>
    );
  }
);
Text.displayName = 'Text';

/* ========================================
   CAPTION/HELPER TEXT
   ======================================== */

export const captionVariants = cva('text-xs', {
  variants: {
    color: {
      // Same verify-ds ink-on-fill contrast floor as textVariants.muted/subtle above:
      // slate-500 and slate-400 fail AA at this (12px) size against these surfaces.
      default: 'text-text-muted',
      muted: 'text-text-muted',
      error: 'text-error-600',
      success: 'text-success-600',
      warning: 'text-warning-600',
      info: 'text-info-600',
    },
  },
  defaultVariants: {
    color: 'default',
  },
});

export type CaptionVariants = VariantProps<typeof captionVariants>;

export interface CaptionProps
  extends Omit<HTMLAttributes<HTMLSpanElement>, 'color'>,
    CaptionVariants {}

export const Caption = forwardRef<HTMLSpanElement, CaptionProps>(
  ({ color, className, children, ...props }, ref) => {
    return (
      <span
        ref={ref}
        className={cn(captionVariants({ color }), className)}
        {...props}
      >
        {children}
      </span>
    );
  }
);
Caption.displayName = 'Caption';

/* ========================================
   CODE/MONO TEXT
   ======================================== */

export const codeVariants = cva('font-mono', {
  variants: {
    size: {
      xs: 'text-xs',
      sm: 'text-sm',
      base: 'text-base',
    },
    variant: {
      inline:
        'px-1.5 py-0.5 rounded bg-muted text-text-primary',
      block:
        'block p-4 rounded-lg bg-background text-text-primary overflow-x-auto',
      plain: 'text-text-secondary',
    },
  },
  defaultVariants: {
    size: 'sm',
    variant: 'inline',
  },
});

export type CodeVariants = VariantProps<typeof codeVariants>;

export interface CodeProps extends HTMLAttributes<HTMLElement>, CodeVariants {}

export const Code = forwardRef<HTMLElement, CodeProps>(
  ({ size, variant, className, children, ...props }, ref) => {
    const Component = variant === 'block' ? 'pre' : 'code';
    return (
      <Component
        ref={ref as Ref<HTMLPreElement> & Ref<HTMLElement>}
        className={cn(codeVariants({ size, variant }), className)}
        {...props}
      >
        {children}
      </Component>
    );
  }
);
Code.displayName = 'Code';

/* ========================================
   LINK TEXT
   ======================================== */

export const textLinkVariants = cva('transition-colors duration-150 cursor-pointer', {
  variants: {
    variant: {
      default: [
        'text-text-primary font-medium',
        'hover:text-text-muted',
        'underline-offset-4 hover:underline',
      ],
      muted: [
        'text-text-muted font-medium',
        'hover:text-text-primary',
        'underline-offset-4 hover:underline',
      ],
      subtle: [
        'text-text-muted',
        'hover:text-text-primary',
      ],
      brand: [
        'text-brand-700 font-medium',
        'hover:text-brand-800',
        'underline-offset-4 hover:underline',
      ],
    },
    size: {
      xs: 'text-xs',
      sm: 'text-sm',
      base: 'text-base',
      lg: 'text-lg',
    },
  },
  defaultVariants: {
    variant: 'default',
    size: 'sm',
  },
});

export type TextLinkVariants = VariantProps<typeof textLinkVariants>;

export interface TextLinkProps
  extends AnchorHTMLAttributes<HTMLAnchorElement>,
    TextLinkVariants {}

export const TextLink = forwardRef<HTMLAnchorElement, TextLinkProps>(
  ({ variant, size, className, children, ...props }, ref) => {
    return (
      <a
        ref={ref}
        className={cn(textLinkVariants({ variant, size }), className)}
        {...props}
      >
        {children}
      </a>
    );
  }
);
TextLink.displayName = 'TextLink';

/* ========================================
   CONVENIENCE EXPORTS
   ======================================== */

// Page-level typography presets
export const PageTitle = forwardRef<
  HTMLHeadingElement,
  Omit<HeadingProps, 'level' | 'as'>
>((props, ref) => <Heading ref={ref} level="h1" as="h1" {...props} />);
PageTitle.displayName = 'PageTitle';

export const PageDescription = forwardRef<
  HTMLParagraphElement,
  Omit<TextProps, 'color' | 'size'>
>((props, ref) => <Text ref={ref} size="sm" color="secondary" {...props} />);
PageDescription.displayName = 'PageDescription';

export const SectionTitle = forwardRef<
  HTMLHeadingElement,
  Omit<HeadingProps, 'level' | 'as'>
>((props, ref) => <Heading ref={ref} level="h2" as="h2" {...props} />);
SectionTitle.displayName = 'SectionTitle';

export const SectionDescription = forwardRef<
  HTMLParagraphElement,
  Omit<TextProps, 'color' | 'size'>
>((props, ref) => <Text ref={ref} size="sm" color="muted" {...props} />);
SectionDescription.displayName = 'SectionDescription';

// Note: CardTitle and CardDescription are defined in Card molecule

// Table cell text presets
export const TableCellPrimary = forwardRef<
  HTMLParagraphElement,
  Omit<TextProps, 'size' | 'weight'>
>((props, ref) => (
  <Text ref={ref} size="sm" weight="medium" color="default" as="span" {...props} />
));
TableCellPrimary.displayName = 'TableCellPrimary';

export const TableCellSecondary = forwardRef<
  HTMLParagraphElement,
  Omit<TextProps, 'size' | 'color'>
>((props, ref) => <Text ref={ref} size="sm" color="muted" as="span" {...props} />);
TableCellSecondary.displayName = 'TableCellSecondary';

// Form helper text
export const HelperText = forwardRef<HTMLSpanElement, CaptionProps>((props, ref) => (
  <Caption ref={ref} color="default" {...props} />
));
HelperText.displayName = 'HelperText';

export const ErrorText = forwardRef<HTMLSpanElement, CaptionProps>((props, ref) => (
  <Caption ref={ref} color="error" {...props} />
));
ErrorText.displayName = 'ErrorText';
